import { useQuery } from '@apollo/client'
import { Clear } from '@mui/icons-material'
import {
    Alert,
    Box,
    Chip,
    IconButton,
    Pagination,
    Stack,
    TextField,
} from '@mui/material'
import { type FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import { codec, useUrlState } from 'shared/hooks/useUrlState'
import { BotLogLevel } from '__generated__/graphql'
import { BotAuditLogs } from './BotAudit.gql'
import { LogPanel, type LogRow } from './LogPanel'

const PAGE_SIZE = 100
const VALID_LEVELS: BotLogLevel[] = [
    BotLogLevel.Error,
    BotLogLevel.Warn,
    BotLogLevel.Info,
    BotLogLevel.Debug,
]
const isValidLevel = (v: string): v is BotLogLevel =>
    (VALID_LEVELS as readonly string[]).includes(v)

const toValidDate = (s: string | undefined): Date | undefined => {
    if (!s) return undefined
    const d = new Date(s)
    return isNaN(d.getTime()) ? undefined : d
}
// Local end-of-day constructor avoids the DST edge case where a day is
// 23 or 25 hours long (adding 86400000ms misses an hour on DST transitions).
const toEndOfDay = (d: Date | undefined): Date | undefined =>
    d
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
        : undefined

const filterCodec = {
    search: codec.string(),
    levels: codec.stringArray({ defaultValue: [] }),
    startDate: codec.string(),
    endDate: codec.string(),
    page: codec.int({ defaultValue: 1 }),
}

export const LogsTab: FC = () => {
    const { state, setState, isDefault } = useUrlState(filterCodec)
    const navigate = useNavigate()

    const levels = state.levels.filter(isValidLevel)
    const startDate = toValidDate(state.startDate)
    const endDate = toEndOfDay(toValidDate(state.endDate))

    const { data, loading, error } = useQuery(BotAuditLogs, {
        variables: {
            startDate,
            endDate,
            levels: levels.length ? levels : undefined,
            search: state.search || undefined,
            page: state.page ?? 1,
            limit: PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    })

    const total = data?.botAuditLogs.total ?? 0
    const rows = (data?.botAuditLogs.rows ?? []) as LogRow[]
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

    const toggleLevel = (lvl: string) => {
        const next = state.levels.includes(lvl)
            ? state.levels.filter((x) => x !== lvl)
            : [...state.levels, lvl]
        setState({ levels: next, page: 1 })
    }

    const openConversation = (conversationId: string) => {
        const params = new URLSearchParams({
            tab: 'conversations',
            conversationId,
        })
        navigate(`${recruiterRouteConfig.botAudit.path}?${params.toString()}`)
    }

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
                data-testid="bot-audit-logs-filters"
            >
                <TextField
                    size="small"
                    label="Search message"
                    value={state.search ?? ''}
                    onChange={(e) =>
                        setState({ search: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 220 }}
                    data-testid="bot-audit-logs-filter-search"
                />
                <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={state.startDate ?? ''}
                    onChange={(e) =>
                        setState({ startDate: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 150 }}
                    data-testid="bot-audit-logs-filter-start-date"
                />
                <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={state.endDate ?? ''}
                    onChange={(e) =>
                        setState({ endDate: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 150 }}
                    data-testid="bot-audit-logs-filter-end-date"
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                        sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            mr: 0.5,
                        }}
                    >
                        Level:
                    </Box>
                    {VALID_LEVELS.map((lvl) => {
                        const active = state.levels.includes(lvl)
                        return (
                            <Chip
                                key={lvl}
                                label={lvl}
                                size="small"
                                clickable
                                onClick={() => toggleLevel(lvl)}
                                color={active ? 'primary' : 'default'}
                                variant={active ? 'filled' : 'outlined'}
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                                data-testid={`bot-audit-logs-filter-level-${lvl}`}
                            />
                        )
                    })}
                </Stack>
                {!isDefault ? (
                    <IconButton
                        size="small"
                        onClick={() =>
                            setState({
                                search: undefined,
                                levels: [],
                                startDate: undefined,
                                endDate: undefined,
                                page: 1,
                            })
                        }
                        title="Clear filters"
                        data-testid="bot-audit-logs-filters-clear"
                    >
                        <Clear fontSize="small" />
                    </IconButton>
                ) : null}
            </Stack>

            {error ? (
                <Alert severity="error">
                    Failed to load logs: {error.message}
                </Alert>
            ) : null}

            <Box
                sx={{
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    opacity: loading ? 0.5 : 1,
                    transition: 'opacity 0.15s',
                }}
            >
                <LogPanel
                    logs={rows}
                    emptyText={
                        loading
                            ? 'Loading...'
                            : 'No logs matched these filters.'
                    }
                    onConversationClick={openConversation}
                />
            </Box>

            {pageCount > 1 ? (
                <Stack direction="row" justifyContent="center">
                    <Pagination
                        size="small"
                        count={pageCount}
                        page={state.page ?? 1}
                        onChange={(_, p) => setState({ page: p })}
                        data-testid="bot-audit-logs-pagination"
                    />
                </Stack>
            ) : null}

            <Box
                sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    textAlign: 'right',
                }}
            >
                {total} log{total === 1 ? '' : 's'}
            </Box>
        </Stack>
    )
}
